import { useUpdated } from "@common/lib/hooks";
import { Common } from "@common/types";
import * as React from "react";

export type FetchOptionsArgs = {
  search: string;
  page: number;
  size: number;
};
export type FetchOptionsResult<TOption> = {
  items: TOption[];
  hasMore: boolean;
};
export type FetchOptions<TOption> = (args: FetchOptionsArgs) => Promise<FetchOptionsResult<TOption>>;

export type WithLazyComponentProps = {};

export type WithLazyComponentAddedProps<TOption> = Common.WithOptions<TOption> & {
  allOptions: TOption[];
  hasMore: boolean;
  infiniteLoaderKey?: string;
  isLoading?: boolean;
  isFirstLoad?: boolean;
  loadMore: () => Promise<void>;
};

export type WithLazyHOCProps<TOption> = {
  preselectedOptions?: TOption[];
  fetchOptions: FetchOptions<TOption>;
  refreshKey?: string | null;
  searchText?: string;
};

type State<TOption> = {
  hasMore: boolean;
  infiniteLoaderKey: string;
  isLoading: boolean;
  isFirstLoad: boolean;
  options: TOption[];
  page: number;
  shouldLoad: boolean;
};

export const withLazyHOC =
  <TOption, TProps extends WithLazyComponentProps>(
    Component: React.ComponentType<TProps & WithLazyComponentAddedProps<TOption>>,
    comparer: (a: TOption, b: TOption) => boolean
  ): React.FunctionComponent<
    Omit<TProps & WithLazyHOCProps<TOption>, "allOptions" | "hasMore" | "isLoading" | "loadMore" | "options">
  > =>
  ({
    fetchOptions,
    preselectedOptions,
    refreshKey,
    searchText,
    ...props
  }: WithLazyHOCProps<TOption> & WithLazyComponentProps) => {
    const [state, setState] = React.useState<State<TOption>>({
      hasMore: true,
      infiniteLoaderKey: "loader",
      isLoading: false,
      shouldLoad: true,
      options: preselectedOptions ?? [],
      isFirstLoad: true,
      page: 0
    });

    // React.useEffect(() => {
    // 	if (!preselectedOptions) return;
    // 	// TODO
    // 	const newOptions = preselectedOptions.filter((x) => !options.some((y) => comparer(x, y)));
    // 	if (newOptions) {
    // 		setOptions([...newOptions, ...options]);
    // 	}
    // }, [preselectedOptions]);

    const load = React.useCallback(
      async (page: number, searchText: string | undefined) => {
        setState({ ...state, isLoading: true });

        const optionsPage = await fetchOptions({
          page,
          search: searchText ?? "",
          size: 20
        });
        const hasMore = optionsPage.hasMore;
        setState({
          ...state,
          infiniteLoaderKey: searchText ?? "loader",
          isLoading: false,
          isFirstLoad: false,
          hasMore,
          options: page === 1 ? optionsPage.items : [...state.options, ...optionsPage.items],
          page
        });
      },
      [fetchOptions, state, searchText]
    );

    const loadMore = React.useCallback(async () => {
      if (!state.shouldLoad) return;

      load(state.page + 1, searchText);
    }, [load, state.page, searchText, state.shouldLoad]);

    useUpdated(() => {
      load(1, searchText);
    }, [refreshKey, searchText]);

    return <Component {...(props as TProps)} {...state} allOptions={state.options} loadMore={loadMore} />;
  };
